import {
  Button, CircularProgress, Grid,
  Menu, MenuItem, Typography, Box,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { displayChildAge, displayFacilityType, displayFamilyType } from '../../modules/data';
import states from '../../modules/states';
import { toPercent } from '../../modules/text';
import MessageBox from '../MessageBox';
import ResponsiveContainer from '../ResponsiveContainer';
import { getMedianHouseholdIncome, getRatesByState } from './data';

function PercentOfPriceAndPercentOfMedianIncome() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [pieData, setPieData] = useState([]);
  const [percentValue, setPercentValue] = useState(25);
  const { familyType, settingType, childAge } = useContext(FiltersContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const { primaryColor, contrastColor } = useContext(StoryContext);

  const [error, setError] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(null);
  const [averageIncomeData, setAverageIncomeData] = useState(null);
  const [averageIncome, setAverageIncome] = useState(0);
  const [payPercent, setPayPercent] = useState(0);

  useEffect(() => {
    setReady(false);
    setError(null);

    Promise.all([getRatesByState(stateCode, null, { settingType, childAge }), getMedianHouseholdIncome(stateCode)])
      .then((responses) => {
        setMonthlyPrice(responses[0].parsedBody.dataProvider[0].average_rate / 12);
        setAverageIncomeData(responses[1].parsedBody.dataProvider[0]);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setReady(true);
      });
  }, [settingType, childAge, stateCode]);

  useEffect(() => {
    if (averageIncomeData) {
      switch (familyType.toLowerCase()) {
        case 'all':
          setAverageIncome(((percentValue / 100) * averageIncomeData.inc_hh) / 12);
          break;

        case 'married-couple family':
          setAverageIncome(((percentValue / 100) * averageIncomeData.inc_fam_married) / 12);
          break;

        case 'single-parent family':
          setAverageIncome(((percentValue / 100) * averageIncomeData.inc_fam_nospouse) / 12);
          break;

        default:
          setAverageIncome(((percentValue / 100) * averageIncomeData.inc_hh) / 12);
          break;
      }
    }
  }, [familyType, averageIncomeData, percentValue, settingType]);

  useEffect(() => {
    const percent = (monthlyPrice / averageIncome) * 100;
    setPayPercent(percent);
    setPieData([{ name: 'Child Care', value: percent }, { name: 'Other', value: 100 - percent }]);
  }, [averageIncome, monthlyPrice]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePercentChange = (event) => {
    setPercentValue(event.target.value);
    handleClose();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  function renderSelectValues() {
    const items = [];
    for (let i = 25; i <= 300; i += 25) {
      items.push(<MenuItem onClick={handlePercentChange} key={i} value={i}>{i}%</MenuItem>);
    }
    return items;
  }

  const renderCustomizedLabel = ({
    cx, cy, percent, index,
  }) => {
    if (index > 0) return null; // only need to show the first value in the center of the donut

    return (
      <text x={cx} y={cy} fontSize="3em" fill={primaryColor} textAnchor="middle" dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={12} md={8}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <MessageBox>
          <Typography variant="body1" align="left">
            In  <b>{states[stateCode.toUpperCase()]}</b>,
            a <b>{displayFamilyType(familyType).toLowerCase()}</b> with
            at least one child under 6 and earning <b>{percentValue}%</b> of median household income pays <b>{toPercent(payPercent / 100)}</b> of
            their income on <b>{displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b> for every <b>{displayChildAge(childAge).toLowerCase()}</b> enrolled.
          </Typography>
        </MessageBox>
      </Grid>
      <Grid item xs={6} style={{ textAlign: 'center' }}>
        <Box displayPrint="none">
          <Button size="small" color="primary" variant="contained" aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} endIcon={<ExpandMoreIcon />}>
            {percentValue} % of median income
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {renderSelectValues()}
          </Menu>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <ResponsiveContainer height={270}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              startAngle={0}
              innerRadius={80}
              outerRadius={110}
              label={renderCustomizedLabel}
              labelLine={false}
              fill={primaryColor}
            >
              {pieData.map((entry, index) => (
                <Cell key={entry} fill={[primaryColor, contrastColor][index]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
}

export default PercentOfPriceAndPercentOfMedianIncome;
